/**
 * Минимальный inline-набор иконок для Gen 4–5.
 *
 * SVG-path'ы взяты из Lucide (MIT — https://lucide.dev/license).
 * Все иконки — 24×24 viewBox, stroke-based, `stroke="currentColor"`,
 * отрисовываются компонентом `BaseIcon.vue`.
 */

import type { IconDef, IconName } from '@/types/component-types';

export type { IconDef, IconName } from '@/types/component-types';

const STROKE: Readonly<Record<IconName, IconDef>> = {
  'arrow-right': {
    viewBox: '0 0 24 24',
    body: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  },
  'arrow-up': {
    viewBox: '0 0 24 24',
    body: '<path d="M12 19V5"/><path d="m5 12 7-7 7 7"/>',
  },
  'chevron-down': {
    viewBox: '0 0 24 24',
    body: '<path d="m6 9 6 6 6-6"/>',
  },
  'chevron-left': {
    viewBox: '0 0 24 24',
    body: '<path d="m15 18-6-6 6-6"/>',
  },
  'chevron-right': {
    viewBox: '0 0 24 24',
    body: '<path d="m9 18 6-6-6-6"/>',
  },
  globe: {
    viewBox: '0 0 24 24',
    body: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  },
  menu: {
    viewBox: '0 0 24 24',
    body: '<line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/>',
  },
  close: {
    viewBox: '0 0 24 24',
    body: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  },
  check: {
    viewBox: '0 0 24 24',
    body: '<path d="M20 6 9 17l-5-5"/>',
  },
  minus: {
    viewBox: '0 0 24 24',
    body: '<path d="M6 12h12"/>',
  },
  external: {
    viewBox: '0 0 24 24',
    body: '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  },
  play: {
    viewBox: '0 0 24 24',
    body: '<polygon points="6 3 20 12 6 21 6 3"/>',
  },
  search: {
    viewBox: '0 0 24 24',
    body: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  },
  info: {
    viewBox: '0 0 24 24',
    body: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  },
  warning: {
    viewBox: '0 0 24 24',
    body: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  },
  danger: {
    viewBox: '0 0 24 24',
    body: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  },
  success: {
    viewBox: '0 0 24 24',
    body: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/>',
  },
  spinner: {
    viewBox: '0 0 24 24',
    body: '<path d="M21 12a9 9 0 1 1-6.219-8.56"/>',
  },
  users: {
    viewBox: '0 0 24 24',
    body: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/>',
  },
  receipt: {
    viewBox: '0 0 24 24',
    body: '<path d="M12 17V7"/><path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8"/><path d="M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z"/>',
  },
  gift: {
    viewBox: '0 0 24 24',
    body: '<path d="M12 7v14"/><path d="M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8"/><path d="M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5"/><rect x="3" y="7" width="18" height="4" rx="1"/>',
  },
  'message-circle': {
    viewBox: '0 0 24 24',
    body: '<path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"/>',
  },
  'bar-chart-3': {
    viewBox: '0 0 24 24',
    body: '<path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
  },
  'user-plus': {
    viewBox: '0 0 24 24',
    body: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/>',
  },
  'trending-up': {
    viewBox: '0 0 24 24',
    body: '<path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/>',
  },
  send: {
    viewBox: '0 0 24 24',
    body: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/>',
  },
  'credit-card': {
    viewBox: '0 0 24 24',
    body: '<rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>',
  },
  trophy: {
    viewBox: '0 0 24 24',
    body: '<path d="M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978"/><path d="M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978"/><path d="M18 9h1.5a1 1 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z"/><path d="M6 9H4.5a1 1 0 0 1 0-5H6"/>',
  },
  ticket: {
    viewBox: '0 0 24 24',
    body: '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>',
  },
  'share-2': {
    viewBox: '0 0 24 24',
    body: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>',
  },
  megaphone: {
    viewBox: '0 0 24 24',
    body: '<path d="M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"/><path d="M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14"/><path d="M8 6v8"/>',
  },
  dices: {
    viewBox: '0 0 24 24',
    body: '<rect width="12" height="12" x="2" y="10" rx="2" ry="2"/><path d="m17.92 14 3.5-3.5a2.24 2.24 0 0 0 0-3l-5-4.92a2.24 2.24 0 0 0-3 0L10 6"/><path d="M6 18h.01"/><path d="M10 14h.01"/><path d="M15 6h.01"/><path d="M18 9h.01"/>',
  },
  'file-spreadsheet': {
    viewBox: '0 0 24 24',
    body: '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M8 13h2"/><path d="M14 13h2"/><path d="M8 17h2"/><path d="M14 17h2"/>',
  },
  smartphone: {
    viewBox: '0 0 24 24',
    body: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
  },
};

export const ICONS: Readonly<Record<IconName, IconDef>> = STROKE;

export function getIcon(name: IconName): IconDef {
  return ICONS[name];
}
